package top.wcpe.allincore.ui.runtime.ir.api

import top.wcpe.allincore.ui.runtime.nbt.NbtCompound
import top.wcpe.allincore.ui.runtime.nbt.NbtList
import top.wcpe.allincore.ui.runtime.nbt.NbtString
import top.wcpe.allincore.ui.runtime.nbt.NbtTag
import top.wcpe.allincore.ui.runtime.nbt.nbtListItems
import top.wcpe.allincore.ui.runtime.nbt.nbtMapBridgeToMap
import top.wcpe.allincore.ui.runtime.nbt.nbtToSnbt
import top.wcpe.allincore.ui.runtime.reactive.Reactive
import top.wcpe.allincore.ui.runtime.reactive.Signal
import kotlin.jvm.JvmField
import kotlin.jvm.JvmStatic

/** DSL 可读的物品视图。 */
class ItemView private constructor(private val signal: Signal<ClientItemStack>) {
    companion object {
        private const val EMPTY_MAX_STACK_SIZE = 0
        private const val DEFAULT_MAX_STACK_SIZE = 64
        private const val SMALL_MAX_STACK_SIZE = 16
        private const val SINGLE_MAX_STACK_SIZE = 1

        private val STACK_SIZE_16 = setOf(
            "minecraft:snowball",
            "minecraft:ender_pearl",
            "minecraft:egg",
            "minecraft:bucket",
            "minecraft:water_bucket",
            "minecraft:lava_bucket",
            "minecraft:milk_bucket",
        )
        private val STACK_SIZE_1 = setOf(
            "minecraft:bow",
            "minecraft:fishing_rod",
            "minecraft:sword",
            "minecraft:shield",
        )

        @JvmField val EMPTY: ItemView = ItemView(Reactive.signal(ClientItemStack.EMPTY))

        @JvmStatic fun of(signal: Signal<ClientItemStack>?): ItemView = if (signal == null) EMPTY else ItemView(signal)

        /** 由一个静态物品快照构造只读视图（全局 tooltip 的 itemstack host 取数用，无响应式来源）。 */
        @JvmStatic
        fun ofStatic(stack: ClientItemStack?): ItemView = if (stack == null) EMPTY else ItemView(Reactive.signal(stack))

        private fun maxStackSizeOf(itemId: String?): Int {
            if (itemId.isNullOrEmpty()) return EMPTY_MAX_STACK_SIZE
            val id = itemId
            if (id in STACK_SIZE_1 || id.hasToolSuffix()) return SINGLE_MAX_STACK_SIZE
            if (id in STACK_SIZE_16 || id.endsWith("_banner")) return SMALL_MAX_STACK_SIZE
            return DEFAULT_MAX_STACK_SIZE
        }

        private fun String.hasToolSuffix(): Boolean = this.endsWith("_sword") ||
            this.endsWith("_pickaxe") ||
            this.endsWith("_axe") ||
            this.endsWith("_shovel") ||
            this.endsWith("_hoe") ||
            this.endsWith("_boat")
    }

    fun current(): ClientItemStack = signal.get()
    fun isEmpty(): Boolean = current().isEmpty()
    fun itemId(): String = current().itemId()
    fun count(): Int = current().count()
    fun displayName(): String? = current().displayName()
    fun lore(): List<String> = current().lore()
    fun enchanted(): Boolean = current().enchanted()
    fun cooldown(): Float = current().cooldown()
    fun durability(): Float? = current().durability()

    fun nbt(path: String?): String? {
        val node = current().nbt().resolve(path ?: "")
        if (node is NbtString) return node.value()
        return node?.let { nbtToSnbt(it) }
    }

    fun nbtJson(path: String?): Map<String, Any>? {
        val node = current().nbt().resolve(path ?: "") ?: return null
        return if (node is NbtCompound) nbtMapBridgeToMap(node) else null
    }

    fun nbtList(path: String?): List<Any?>? {
        val node = current().nbt().resolve(path ?: "") ?: return null
        if (node !is NbtList) return null
        return nbtListItems(node).map { convertTagForList(it) }
    }

    fun nbtAll(): Map<String, Any> {
        val root = current().nbt()
        return if (root.isEmpty()) emptyMap() else nbtMapBridgeToMap(root)
    }

    fun snbt(): String? {
        val root = current().nbt()
        return if (root.isEmpty()) null else nbtToSnbt(root)
    }

    fun maxStackSize(): Int = Companion.maxStackSizeOf(itemId())

    fun readField(member: String): Any? = when (member) {
        "isEmpty" -> isEmpty()
        "itemId" -> itemId()
        "count" -> count()
        "displayName" -> displayName()
        "lore" -> lore()
        "enchanted" -> enchanted()
        "cooldown" -> cooldown()
        "durability" -> durability()
        "maxStackSize" -> maxStackSize()
        "nbtAll" -> nbtAll()
        "snbt" -> snbt()
        else -> null
    }

    private fun convertTagForList(item: NbtTag?): Any? = when (item) {
        null -> null
        is NbtCompound -> nbtMapBridgeToMap(item)
        is NbtList -> nbtListItems(item).map { convertTagForList(it) }
        is NbtString -> item.value()
        else -> nbtToSnbt(item)
    }
}
